/**
 * RequestDateChangeModal — F-S2.
 *
 * PM/SA modal to request a change to a project date. Shows the current (read-only) date, a new-date
 * picker and a mandatory reason (min 20, max 500 chars, live count). Submit is disabled until the
 * reason is long enough and a new date is chosen. On success it POSTs to
 * /api/projects/{projectId}/date-change-requests and toasts "Request submitted for leadership approval".
 */
import { type FormEvent, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

import type { DateChangeField } from "@/types";
import { useCreateDateChangeRequest } from "@/hooks/useDateChangeRequests";
import { formatDateValue } from "@/lib/date-change";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const REASON_MIN = 20;
const REASON_MAX = 500;

export interface RequestDateChangeModalProps {
  open: boolean;
  projectId: string;
  dateField: string;
  currentValue: string | null;
  label: string;
  onSubmitted: () => void;
  onClose: () => void;
}

export function RequestDateChangeModal({
  open,
  projectId,
  dateField,
  currentValue,
  label,
  onSubmitted,
  onClose,
}: RequestDateChangeModalProps): JSX.Element {
  const createRequest = useCreateDateChangeRequest(projectId);

  const [newDate, setNewDate] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Re-seed the form each time the modal opens for a (possibly different) field.
  useEffect(() => {
    if (open) {
      setNewDate("");
      setReason("");
      setError(null);
    }
  }, [open, dateField]);

  const trimmedReason = reason.trim();
  const reasonValid =
    trimmedReason.length >= REASON_MIN && trimmedReason.length <= REASON_MAX;
  const canSubmit = !!newDate && reasonValid && !createRequest.isPending;

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    setError(null);

    if (!newDate) {
      setError("Please choose a new date.");
      return;
    }
    if (!reasonValid) {
      setError(
        `Reason must be between ${REASON_MIN} and ${REASON_MAX} characters.`,
      );
      return;
    }

    createRequest.mutate(
      {
        date_field: dateField as DateChangeField,
        requested_value: newDate,
        reason: trimmedReason,
      },
      {
        onSuccess: () => {
          toast.success("Request submitted for leadership approval");
          onSubmitted();
          onClose();
        },
        onError: (err: unknown) => {
          const msg =
            err && typeof err === "object" && "response" in err
              ? (err as { response?: { data?: { message?: string } } }).response
                  ?.data?.message
              : undefined;
          setError(msg || "Failed to submit the request. Please try again.");
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Request Date Change — {label}</DialogTitle>
          <DialogDescription>
            Your request will be sent to leadership for approval. The current
            date is unchanged until it is approved.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 py-1">
          <div className="space-y-1.5">
            <Label>Current date</Label>
            <Input value={formatDateValue(currentValue)} readOnly disabled />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="dcr-new-date">New date</Label>
            <DatePicker
              id="dcr-new-date"
              value={newDate}
              onChange={setNewDate}
              aria-describedby={currentValue ? "dcr-current-hint" : undefined}
              required
            />
            {currentValue && (
              <p id="dcr-current-hint" className="text-xs text-text-secondary">
                Currently {formatDateValue(currentValue)}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="dcr-reason">Reason for change</Label>
              <span
                className={cn(
                  "text-xs tabular-nums",
                  reasonValid
                    ? "text-text-secondary"
                    : "text-amber-600 dark:text-amber-400",
                )}
              >
                {trimmedReason.length}/{REASON_MAX}
              </span>
            </div>
            <Textarea
              id="dcr-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value.slice(0, REASON_MAX))}
              placeholder="Explain why this date needs to change (minimum 20 characters)…"
              rows={4}
              maxLength={REASON_MAX}
              aria-invalid={reason.length > 0 && !reasonValid}
              required
            />
            {reason.length > 0 && trimmedReason.length < REASON_MIN && (
              <p className="text-xs text-amber-600 dark:text-amber-400">
                {REASON_MIN - trimmedReason.length} more character
                {REASON_MIN - trimmedReason.length === 1 ? "" : "s"} required.
              </p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={createRequest.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {createRequest.isPending && (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              )}
              {createRequest.isPending ? "Submitting…" : "Submit request"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default RequestDateChangeModal;
